<template>
  <main data-news-v="3.2.4">
    <template v-if="data?.hasVisibilitySet && visibilityStatus === 'checking'">
      <div
        style="
          min-height: 480px;
          display: flex;
          align-items: center;
          justify-content: center;
        "
      >
        <LoaderDots />
      </div>
    </template>

    <template
      v-else-if="data?.hasVisibilitySet && visibilityStatus === 'unknown'"
    >
      <div
        style="
          min-height: 480px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 20px;
        "
      >
        <div style="max-width: 480px; text-align: left">
          <h2 style="margin: 0 0 8px; font-size: 20px">
            We need one quick setting before we can show this article
          </h2>

          <p style="margin: 0 0 16px; line-height: 1.5">
            This page is restricted by region. We can’t confirm access until
            your country/region preference is set.
          </p>

          <div
            style="
              display: inline-flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
            "
          >
            <BaseButton type="button" @click="onRequestLocaleSetter">
              Set my region
            </BaseButton>
          </div>

          <p style="margin: 14px 0 0; font-size: 13px; opacity: 0.8">
            If you use a browser setting that blocks cookies, you may need to
            allow site preferences for this page to load correctly.
          </p>
        </div>
      </div>
    </template>

    <template v-else-if="visibilityStatus === 'visible'">
      <ArticleHero v-if="data?.article" :data="data?.article" />

      <ContentJourneyFlyout
        :class="$style.mobileJourneyFlyout"
        :journeys="contentJourneys"
        :loading="isContentJourneyLoading"
        :active-journey-id="activeJourneyId"
      />

      <WithAsideCards
        :data="data?.article?.fields?.cards"
        style="--background-color: var(--white)"
      >
        <div :class="$style.bodyLayout">
          <aside
            v-if="
              contentJourneys.length > 0 ||
              isContentJourneyLoading ||
              tableOfContentsMode !== 'off'
            "
            :class="$style.navigationAside"
          >
            <div :class="$style.desktopJourneys">
              <ContentJourney
                v-for="journey in contentJourneys"
                :key="journey.id"
                :journey="journey"
                :initially-expanded="
                  shouldExpandContentJourney(journey.id, activeJourneyId)
                "
              />
              <ContentJourney
                v-if="isContentJourneyLoading && !contentJourneys.length"
                :loading="true"
              />
            </div>
            <ArticlePeak
              v-if="tableOfContentsMode !== 'off'"
              :class="$style.peak"
              :table-of-contents="tableOfContents"
            />
          </aside>
          <ArticleBody
            v-if="computedArticleBody"
            :data="computedArticleBody"
            :drop-cap="computedArticleDropCap"
            :article-id="data?.article?.sys?.id"
          />
        </div>
      </WithAsideCards>

      <ArticleVoteBar :article-id="data?.article?.sys?.id" />

      <RelatedArticles
        v-if="data?.article?.fields?.relatedArticles?.length"
        :data="data?.article?.fields?.relatedArticles"
      />

      <RelatedArticles
        v-else-if="data?.autoRelatedArticles?.length"
        :data="data?.autoRelatedArticles"
      />
    </template>
  </main>
</template>

<script setup lang="ts">
import { clearNuxtData } from '#app'
import type { Entry } from 'contentful'

import { arrayAsString } from '~/utils'
import { buildRedirectUrl } from '~/utils/parsePageLevel'
import { requireContentfulEntry } from '~/utils/requireContentfulEntry'
import { resolveUnauthorizedPath } from '~/utils/visibilityRedirect'
import type { TypeArticleSkeleton } from '~/types/contentful'
import { shouldExpandContentJourney } from '~/services/contentJourney'

interface TableOfContentsConfig {
  mode?: 'off' | 'auto' | 'custom'
  customHtml?: string
}

const { $client, $visibilityDecision } = useNuxtApp()
const { locale } = useI18n()
const localePath = useLocalePath()

const route = useRoute()
const slug = computed(() =>
  route.params?.slug ? arrayAsString(route.params.slug) : '',
)
const asyncKey = computed(() => `article-data-${slug.value}-${locale.value}`)
const tableOfContents = computed<TableOfContentsConfig | undefined>(
  () => data.value?.article?.fields?.tableOfContents as TableOfContentsConfig,
)
const tableOfContentsMode = computed(() => tableOfContents.value?.mode ?? 'off')
const activeJourneyId = computed(() => arrayAsString(route.query.journey) || '')

const { data, error, refresh } = await useAsyncData(
  asyncKey,
  async () => {
    const pathCandidates = [slug.value, `/${slug.value}`].filter(
      (value): value is string => Boolean(value),
    )

    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
        content_type: 'article',
        limit: 1,
        include: 10,
        locale: locale.value,
        // exact match to avoid partial matches like "test" -> "wpn-ce-test"
        'fields.path[in]': pathCandidates,
      })

    const articleData = requireContentfulEntry(
      response?.items?.[0],
      'Article not found',
    )

    // Related Articles

    const autoRelatedArticles: Entry<
      TypeArticleSkeleton,
      'WITHOUT_UNRESOLVABLE_LINKS',
      string
    >[] = []

    if (
      !articleData?.fields?.relatedArticles?.length &&
      articleData?.fields?.tags?.length
    ) {
      const tags =
        articleData?.fields?.tags?.filter(
          (tag) => tag?.sys?.contentType?.sys?.id?.toLowerCase?.() === 'tag',
        ) || []

      const concatenatedTags = tags
        .map((t) => t?.sys?.id)
        .filter(Boolean)
        .join(',')

      const limit = 3
      const batchSize = 5
      const maxAttempts = 5

      let skip = 0
      let attempts = 0
      let hasMore = true

      while (
        autoRelatedArticles.length < limit &&
        hasMore &&
        attempts < maxAttempts
      ) {
        attempts += 1

        const response =
          await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>(
            {
              content_type: 'article',
              include: 10,
              locale: locale?.value,
              limit: batchSize,
              skip,
              'fields.tags.sys.id[in]': [concatenatedTags],
              'fields.path[ne]': arrayAsString(route?.params?.slug),
            },
          )

        skip += batchSize
        hasMore = response.items.length === batchSize

        for (const item of response.items) {
          const decision = $visibilityDecision(item.fields?.visibility)
          if (decision.visible !== true) continue

          autoRelatedArticles.push(item)
          if (autoRelatedArticles.length === limit) break
        }
      }
    }

    return {
      article: articleData,
      autoRelatedArticles,
      hasVisibilitySet: !!articleData?.fields?.visibility,
    }
  },
  { watch: [slug, () => locale.value] },
)

const { journeys: contentJourneys, isLoading: isContentJourneyLoading } =
  useContentJourney(() => data.value?.article)

if (error.value) throw error.value

watch(error, (asyncDataError) => {
  if (asyncDataError) showError(asyncDataError)
})

// When slug/locale changes in SPA nav, nuke the old cache + refetch
watch(
  asyncKey,
  async (nextKey, prevKey) => {
    if (prevKey) clearNuxtData(prevKey) // ✅ clear the old cache entry
    clearNuxtData(nextKey) // optional: also clear new key
    await refresh()
  },
  { flush: 'pre' },
)

const computedArticleBody = computed(() => {
  const typedMarkdownEditorReturn = data?.value?.article?.fields?.body as {
    body: string
  }
  return typedMarkdownEditorReturn?.body || ''
})

const computedArticleDropCap = computed(() => {
  return data?.value?.article?.fields?.dropCap || false
})

if (data.value?.article) {
  getPageMetaData(data.value.article, 'article')
}

const { visibilityStatus } = usePageVisibility({
  visibility: () => data.value?.article?.fields?.visibility,
  onDenied: async (decision) => {
    const unauthorizedPath = resolveUnauthorizedPath(decision)
    if (unauthorizedPath) {
      await navigateTo({
        path: localePath(unauthorizedPath),
        query:
          unauthorizedPath === '/unauthorized'
            ? { returnTo: route.fullPath }
            : undefined,
      })
    } else {
      const redirectUrl = buildRedirectUrl(
        locale.value,
        data.value?.article?.fields?.accessDenialRedirectUrl || '/news',
      )

      await navigateTo(redirectUrl)
    }
  },
})

const onRequestLocaleSetter = () => {
  const localeModalStore = useLocalModalStore()
  localeModalStore.showLocaleModal = true
}

const globalStore = useGlobalStore()
watch(
  () => data.value,
  (newVal) => {
    globalStore.pageEntrySys = newVal?.article?.sys
  },
  { immediate: true },
)
</script>

<style lang="scss" module>
.bodyLayout {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;

  @include media('lg') {
    flex-direction: row;
    align-items: flex-start;
    gap: 3%;
  }

  .navigationAside {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;

    @include media('less-than-lg') {
      display: contents;
    }

    @include media('lg') {
      align-self: stretch;
      flex: 0 0 340px;
    }
  }

  .desktopJourneys {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @include media('less-than-lg') {
      display: none;
    }
  }

  .peak {
    width: 100%;
  }
}

.mobileJourneyFlyout {
  display: block;

  @include media('lg') {
    display: none;
  }
}
</style>
